<div class="ai-assistant-overlay" *ngIf="isOpen">
<div class="ai-assistant-panel" [class.expanded]="showPreview && pendingChanges" (click)="$event.stopPropagation()">

  <!-- Preview Render Panel (left side, only when there are pending changes) -->
  <div *ngIf="showPreview && pendingChanges" class="preview-render-panel">
    <div class="preview-render-header">
      <div class="preview-tabs">
        <button class="preview-tab-btn" [class.active]="previewTab === 'preview'" (click)="previewTab = 'preview'">
          <i class="fas fa-eye"></i>
          Preview
        </button>
        <button class="preview-tab-btn" [class.active]="previewTab === 'code'" (click)="previewTab = 'code'">
          <i class="fas fa-code"></i>
          Code
        </button>
      </div>
    </div>

    <div class="preview-render-body">
      <!-- Live Preview (iframe) -->
      <iframe *ngIf="previewTab === 'preview'" #previewIframe class="preview-iframe" sandbox="allow-scripts allow-same-origin"></iframe>

      <!-- Code blocks -->
      <div *ngIf="previewTab === 'code'" class="preview-code-content">
        <!-- Controls Preview -->
        <div *ngIf="pendingChanges.controls" class="preview-section">
          <h4><i class="fas fa-th-large"></i> Controls ({{ pendingChanges.controls.length }})</h4>
          <div class="controls-preview">
            <div class="control-chip" *ngFor="let control of pendingChanges.controls">
              {{ control.name || control.type }}
            </div>
          </div>
        </div>

        <!-- CSS Preview -->
        <div *ngIf="pendingChanges.css" class="preview-section">
          <h4><i class="fas fa-palette"></i> CSS Updates</h4>
          <pre class="code-preview">{{ pendingChanges.css }}</pre>
        </div>

        <!-- JavaScript Preview -->
        <div *ngIf="pendingChanges.javascript" class="preview-section">
          <h4><i class="fab fa-js"></i> JavaScript Updates</h4>
          <pre class="code-preview">{{ pendingChanges.javascript }}</pre>
        </div>

        <!-- HTML Preview -->
        <div *ngIf="pendingChanges.headerHtml || pendingChanges.footerHtml" class="preview-section">
          <h4><i class="fas fa-code"></i> HTML Updates</h4>

          <div *ngIf="pendingChanges.headerHtml" class="html-subsection">
            <h5>Header HTML</h5>
            <pre class="code-preview">{{ pendingChanges.headerHtml }}</pre>
          </div>

          <div *ngIf="pendingChanges.footerHtml" class="html-subsection">
            <h5>Footer HTML</h5>
            <pre class="code-preview">{{ pendingChanges.footerHtml }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div class="preview-render-footer">
      <button class="btn btn-secondary" (click)="rejectChanges()">
        <i class="fas fa-times"></i>
        Discard
      </button>
      <button class="btn btn-primary" (click)="applyChanges()">
        <i class="fas fa-check"></i>
        Apply Changes
      </button>
    </div>
  </div>

  <!-- Chat Panel (right side, always visible) -->
  <div class="chat-panel">
    <!-- Header -->
    <div class="ai-assistant-header">
      <div class="header-title">
        <h3>4Thought AI Agent (Beta v3)</h3>
      </div>
      <div class="header-actions">
        <button class="action-btn" type="button" (click)="clearConversation(); $event.stopPropagation(); $event.preventDefault()" title="Clear conversation" [disabled]="isProcessing">
          <i class="fas fa-trash-alt"></i>
          <span class="action-label">Clear Chat</span>
        </button>
        <button class="close-btn" type="button" (click)="close($event)" title="Close">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Token Usage Bar -->
    <div class="token-usage-bar">
      <div class="token-stat">
        <i class="fas fa-bolt"></i>
        <span class="token-label">Session:</span>
        <span class="token-value">{{ sessionTokens | number }}</span>
      </div>
      <div class="token-stat">
        <i class="fas fa-database"></i>
        <span class="token-label">Total:</span>
        <span class="token-value">{{ userTotalTokens | number }}</span>
      </div>
    </div>

    <!-- Main Content -->
    <div class="ai-assistant-content">
      <!-- Chat Messages -->
      <div class="messages-container">
        <div *ngFor="let message of messages"
             class="message"
             [class.user-message]="message.role === 'user'"
             [class.assistant-message]="message.role === 'assistant'"
             [class.system-message]="message.role === 'system'">
          <div class="message-avatar">
            <i class="fas fa-user" *ngIf="message.role === 'user'"></i>
            <i class="fas fa-brain" *ngIf="message.role === 'assistant'"></i>
            <i class="fas fa-info-circle" *ngIf="message.role === 'system'"></i>
          </div>
          <div class="message-content">
            <div class="message-text" [innerHTML]="(message.displayContent || message.content) | sanitize:'html'"></div>
            <div class="message-timestamp">{{ message.timestamp | date:'shortTime' }}</div>
          </div>
        </div>

        <!-- Processing Indicator -->
        <div *ngIf="isProcessing" class="message assistant-message processing">
          <div class="message-avatar">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <div class="message-content">
            <div class="message-text">Thinking...</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Agent Confirmation Card -->
    <div *ngIf="pendingAgentConfirmation" class="agent-confirmation-card">
      <div class="confirmation-header">
        <mat-icon>warning</mat-icon>
        <span>{{ 'AGENT.CONFIRMATION_REQUIRED' | translate }}</span>
      </div>
      <div class="confirmation-summary">
        {{ pendingAgentConfirmation.summary }}
      </div>
      <div class="confirmation-actions">
        <button mat-raised-button color="primary"
                (click)="confirmAgentAction()"
                [disabled]="isProcessing">
          <mat-icon>check</mat-icon>
          {{ 'AGENT.CONFIRM' | translate }}
        </button>
        <button mat-stroked-button
                (click)="rejectAgentAction()"
                [disabled]="isProcessing">
          <mat-icon>close</mat-icon>
          {{ 'AGENT.CANCEL' | translate }}
        </button>
      </div>
    </div>

    <!-- Agent Result Card: shown after a PreferencePage entity is created -->
    <div *ngIf="lastCreatedPageEntity" class="agent-result-card">
      <div class="agent-result-card__header">
        <i class="fas fa-file-alt agent-result-card__icon"></i>
        <span>{{ 'AGENT.PREFERENCE_PAGE_CREATED' | translate }}</span>
      </div>
      <div class="agent-result-card__body">
        <a class="agent-result-card__link" (click)="navigateToEntity(lastCreatedPageEntity)">
          {{ lastCreatedPageEntity.name }}
          <span class="agent-result-card__action">{{ 'AGENT.VIEW_PAGE' | translate }}</span>
        </a>
        <div *ngIf="lastCreatedChildEntities.length > 0" class="agent-result-card__children">
          <span class="agent-result-card__children-label">{{ 'AGENT.ALSO_CREATED' | translate }}:</span>
          <span *ngFor="let child of lastCreatedChildEntities; trackBy: trackByEntityId"
                class="agent-result-card__child">
            {{ child.entityType }}: {{ child.name }}
          </span>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="ai-assistant-input">
      <!-- Error Display -->
      <div *ngIf="error" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        <span>{{ error }}</span>
      </div>

      <!-- Attached Images -->
      <div *ngIf="attachedImages.length > 0" class="attached-images">
        <div *ngFor="let image of attachedImages; let i = index" class="image-preview">
          <img [src]="getImageThumbnail(image)" [alt]="image.fileName" />
          <button class="remove-image" (click)="removeImage(i)">
            <i class="fas fa-times"></i>
          </button>
          <span class="image-name">{{ image.fileName }}</span>
        </div>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <button *ngFor="let action of quickActions"
                class="quick-action-btn"
                (click)="addQuickAction(action.prompt)"
                title="{{ action.prompt }}">
          <i class="fas {{ action.icon }}"></i>
          <span>{{ action.label }}</span>
        </button>
      </div>

      <!-- Message Input -->
      <div class="input-container">
        <button
          class="attach-button"
          type="button"
          (click)="triggerFileInput()"
          [disabled]="attachedImages.length >= maxImages || isProcessing"
          title="Attach image ({{ attachedImages.length }}/{{ maxImages }})">
          <i class="fas fa-paperclip"></i>
        </button>

        <textarea
          [(ngModel)]="userMessage"
          (keydown)="onKeyDown($event)"
          placeholder="Describe what you want to do... (Shift+Enter for new line)"
          rows="1"
          [disabled]="isProcessing"></textarea>

        <button
          class="send-button"
          type="button"
          (click)="sendMessage()"
          [disabled]="(!userMessage.trim() && attachedImages.length === 0) || isProcessing"
          title="Send message">
          <i class="fas fa-paper-plane"></i>
        </button>

        <input
          #fileInput
          type="file"
          accept="image/*"
          multiple
          (change)="onFileSelected($event)"
          style="display: none;" />
      </div>
    </div>
  </div>

</div>
</div>
